import { Filter, useGetFilters } from '@ais-ui/websockets';
import {
  LoadingSpinner,
  ScrollArea,
  TruncatedText,
  SearchInput,
} from '@ais-ui/ui-core';
import { useFilterContext } from '../../modules/Map/FilterContext';
import { isFilterApplied } from '../../modules/Filters/utils';
import { filterToIcon } from '../../modules/Filters/filterToIcon';
import { cn } from '@ais-ui/utils';
import { useState, useMemo, useCallback } from 'react';
import { useLocalization } from '@ais-ui/locale-management';

interface MobileFilterListProps {
  onFilterClick: () => void;
}

export const MobileFilterList = ({ onFilterClick }: MobileFilterListProps) => {
  const { messages } = useLocalization();
  const { data: filters, isLoading } = useGetFilters();
  const filterContext = useFilterContext();
  const [search, setSearch] = useState<string>('');

  // Filter list based on search query
  const filteredFilters = useMemo(() => {
    if (!filters) return [];
    if (!search.trim()) return filters;

    const searchLower = search.toLowerCase();
    return filters.filter((filter) =>
      filter.name.toLowerCase().includes(searchLower),
    );
  }, [filters, search]);

  const handleFilterClick = useCallback(
    (filter: Filter) => {
      const isApplied = isFilterApplied(filter, filterContext.customFilters);

      if (!isApplied) {
        filterContext.setCustomFilters([
          ...filterContext.customFilters,
          filter,
        ]);
      } else {
        const newFilters = filterContext.customFilters.filter(
          (f) => f.uuid !== filter.uuid,
        );
        filterContext.setCustomFilters(newFilters);
      }

      // Close drawer immediately - AISMapLayer effect handles apply and refresh
      onFilterClick();
    },
    [filterContext, onFilterClick],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (!filters || filters.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <p className="text-muted-foreground">
          {messages.filtersNoFiltersAvailable}
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Search Input */}
      <div className="px-4 pt-4 pb-2 shrink-0 bg-background safe-area-inset-x">
        <SearchInput
          id="filter-search"
          placeholder="Search filters..."
          className="h-12 text-base w-full mx-auto max-w-full"
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          onClear={() => setSearch('')}
        />
      </div>

      {/* Filter List */}
      <ScrollArea className="flex-1">
        <div className="p-4 pt-2 space-y-2 safe-area-inset-x">
          {filteredFilters.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-muted-foreground">
                {messages.filtersNoFiltersMatch}
              </p>
              {search && (
                <p className="text-sm text-muted-foreground mt-2">
                  Try a different search term
                </p>
              )}
            </div>
          ) : (
            filteredFilters.map((filter) => {
              const isApplied = isFilterApplied(
                filter,
                filterContext.customFilters,
              );
              const FilterIcon = filterToIcon(filter);

              return (
                <button
                  key={filter.uuid}
                  onClick={() => handleFilterClick(filter)}
                  className={cn(
                    'w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors',
                    'hover:bg-accent active:bg-accent/80',
                    isApplied && 'bg-primary/10 border-2 border-primary',
                  )}
                >
                  <FilterIcon
                    className={cn(
                      'size-5 shrink-0',
                      isApplied && 'text-primary',
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <TruncatedText
                      text={filter.name}
                      className={cn('font-medium', isApplied && 'text-primary')}
                      maxWidth="280px"
                    />
                  </div>
                  {isApplied && (
                    <div className="shrink-0 size-2 rounded-full bg-primary" />
                  )}
                </button>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
